import { useState } from 'react';

import { appColors, NuggetCard } from '@backrs_ui/common-ui';

import { OfficeHoursMessage, SearchedUser } from '@backrs_ui/grow-ui/src/models/models';
import {
  useLazyGetUserProfileQuery,
  useLazySearchForUserQuery,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';

import { Box, TextField, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

export type UserSelectContact = Readonly<{
  id: string;
  relevance?: string;
  name: string;
  role?: 'professional' | 'student';
  jobTitle?: string;
  employer?: string;
  profileImage?: string;
}>;

type UserSearchProps = Readonly<{
  onClick: (
    message: string,
    LLMInput?: string,
    currentMessageList?: OfficeHoursMessage[],
    sessionId?: number,
  ) => void;
  asSuperuser?: boolean;
  userId?: number;
  sessionId?: number;
}>;

export const UserSearch = ({
  onClick,
  asSuperuser = false,
  userId,
  sessionId,
}: UserSearchProps) => {
  const [userInput, setUserInput] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<SearchedUser[] | null>(null);
  const [searchForUser] = useLazySearchForUserQuery();
  const [getUserProfile] = useLazyGetUserProfileQuery();

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setUserSearchResults(null);

      return;
    }

    if (!userId) {
      return;
    }

    try {
      const data = await searchForUser({
        query,
        user_id: userId,
        full_last_name: asSuperuser,
      }).unwrap();

      setUserSearchResults(data);
    } catch (err) {
      console.error('Error searching for users:', err);
      setUserSearchResults([]);
    }
  };

  const handleUserSelect = async (user: SearchedUser) => {
    await handleUserSelection(
      user.id.toString(),
      user.name,
      onClick,
      asSuperuser,
      sessionId,
      getUserProfile,
    );
    setUserSearchResults(null);
    setUserInput('');
  };

  return (
    <Box>
      <TextField
        id="user-search-input"
        label="Search"
        variant="outlined"
        fullWidth
        value={userInput}
        onChange={(e) => setUserInput(e.target.value)}
        onKeyUp={() => handleSearch(userInput)}
        placeholder="Type to search for users by name"
        sx={textFieldStyles}
      />

      <Box sx={resultsContainerStyles}>
        {userInput && userSearchResults?.length === 0 && <Typography>No results found</Typography>}
        {userSearchResults?.map((user) => (
          <Box
            key={user.id}
            id={`user-search-result-${user.id}`}
            onClick={() => handleUserSelect(user)}
            sx={resultItemStyles}
          >
            <Typography>{user.name}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type UserSelectProps = Readonly<{
  onClick: (
    message: string,
    LLMInput?: string,
    currentMessageList?: OfficeHoursMessage[],
    sessionId?: number,
  ) => void;
  contacts: UserSelectContact[];
  asSuperuser?: boolean;
  sessionId?: number;
}>;

export const UserSelect = ({ contacts, onClick, asSuperuser, sessionId }: UserSelectProps) => {
  const [getUserProfile] = useLazyGetUserProfileQuery();

  const handleContactClick = (contact: UserSelectContact) => {
    handleUserSelection(
      contact.id,
      contact.name,
      onClick,
      asSuperuser || false,
      sessionId,
      getUserProfile,
    );
  };

  return (
    <>
      {contacts.map((contact) => {
        return (
          <Box
            key={contact.id}
            id={`user-search-contact-${contact.id}`}
            onClick={() => handleContactClick(contact)}
            sx={userSelectStyle}
          >
            <Box sx={{ width: '100%' }}>
              <NuggetCard
                id={`user-select-card-${contact.id}`}
                title={contact.name}
                badge={{
                  label: contact.jobTitle
                    ? `${contact.jobTitle}${contact.employer ? ` at ${contact.employer}` : ''}`
                    : 'Profile',
                  iconConfig: contact.profileImage
                    ? { mode: 'avatar', avatarUrl: contact.profileImage }
                    : { mode: 'appIcon', icon: 'User02' },
                }}
                entityName={contact.name}
                imageUrl={contact.profileImage}
              />
            </Box>
          </Box>
        );
      })}
    </>
  );
};

const handleUserSelection = async (
  userId: string,
  name: string,
  onClick: (
    message: string,
    LLMInput?: string,
    currentMessageList?: OfficeHoursMessage[],
    sessionId?: number,
  ) => void,
  asSuperuser: boolean,
  sessionId?: number,
  getUserProfile?: ReturnType<typeof useLazyGetUserProfileQuery>[0],
) => {
  if (!sessionId || !getUserProfile) return;

  try {
    const profile = await getUserProfile({
      user_id: userId,
      as_superuser: asSuperuser,
    }).unwrap();

    onClick(
      `Check in on **${name}**`,
      `Provide a single paragraph summarizing my student ${name}, focusing on their current goals and undertakings.\n\n${profile}`,
      undefined,
      sessionId,
    );
  } catch (err) {
    console.error('Error fetching user profile:', err);
  }
};

const textFieldStyles: SxProps<Theme> = {
  marginBottom: '16px',
  mt: 3,
};

const resultsContainerStyles: SxProps<Theme> = {
  maxHeight: '300px',
  overflowY: 'auto',
};

const resultItemStyles: SxProps<Theme> = {
  padding: '8px',
  cursor: 'pointer',
  borderBottom: '1px solid #eee',
};

const userSelectStyle: SxProps<Theme> = {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '2px',
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: appColors.color.blue1,
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
} as const;
